// Confetti celebration used by the network "Add to network" flow and the
// invite-office modal. Plays a Lottie animation (lazy-loaded player + JSON
// from public/animations) centered on the given viewport origin, falling back
// to a dependency-free CSS burst if either fails to load.

const animationCache = new Map()

// Lazy-loads the Lottie player (own webpack chunk, fetched on first use) and
// the animation JSON. Memoized per path; failures aren't cached so a missing
// file can be retried once it's deployed.
function loadConfettiAnimation(path) {
  if (!animationCache.has(path)) {
    const promise = Promise.all([
      import(/* webpackChunkName: "lottie" */ 'lottie-web/build/player/lottie_light').then(m => m.default),
      fetch(path).then(response => {
        if (!response.ok) throw new Error(`confetti animation not found: ${path}`)
        return response.json()
      })
    ])
    promise.catch(() => animationCache.delete(path))
    animationCache.set(path, promise)
  }

  return animationCache.get(path)
}

export async function launchConfetti(origin, { path = '/animations/confetti.json', size = 300 } = {}) {
  try {
    const [lottie, animationData] = await loadConfettiAnimation(path)
    playLottieConfetti(lottie, animationData, origin, size)
  } catch {
    // Lottie player or animation JSON unavailable - fall back to the CSS burst
    burstConfetti(origin)
  }
}

function playLottieConfetti(lottie, animationData, { x, y }, size) {
  const container = document.createElement('div')
  container.style.cssText = `position: fixed; left: ${x - size / 2}px; top: ${y - size / 2}px;` +
    `width: ${size}px; height: ${size}px; z-index: 2000; pointer-events: none;`
  document.body.appendChild(container)

  const animation = lottie.loadAnimation({
    container: container,
    renderer: 'svg',
    loop: false,
    autoplay: true,
    animationData: animationData
  })

  let cleaned = false
  const cleanup = () => {
    if (cleaned) return
    cleaned = true
    animation.destroy()
    container.remove()
  }
  animation.addEventListener('complete', cleanup)
  setTimeout(cleanup, 8000) // safety net if complete never fires
}

const COLORS = ['#F1616E', '#4168FF', '#408558', '#FFC1C6', '#FFD166', '#D4EFDF']
const EASE_OUT = 'cubic-bezier(0.2, 0.6, 0.4, 1)'

/**
 * Full-screen celebration for the guided tutorial's completion modal, ported
 * from the design prototype (design-prototypes src/lib/confetti.ts).
 *
 * The pre-rendered Lottie burst can't do this — its burst is baked in, and
 * scaling the container only makes the pieces bigger. So the celebration
 * draws its own, three modes running together: falling from the top, exploding
 * up from the bottom, and bursting out from behind the modal.
 *
 * `behindZ` sits just under the celebration overlay (so the burst reads as
 * coming from behind the card) and `frontZ` above it (so the falling and
 * rising pieces pass in front).
 */
export function launchCelebration(origin, { behindZ = 69, frontZ = 2000 } = {}) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

  const w = window.innerWidth
  const h = window.innerHeight
  const behind = layer(behindZ)
  const front = layer(frontZ)

  fall(front, 70, 3200)
  rise(front, 55, 3000)
  burst(behind, origin || { x: w / 2, y: h / 2 - 40 }, 80, 3000)

  // Longest possible delay plus duration, so nothing is cut off mid-fall.
  setTimeout(() => {
    behind.remove()
    front.remove()
  }, 4400)
}

function layer(z) {
  const el = document.createElement('div')
  el.style.cssText = `position: fixed; inset: 0; z-index: ${z}; pointer-events: none; overflow: hidden;`
  document.body.appendChild(el)
  return el
}

// One small piece: a square, a thin strip or a dot.
function celebrationPiece(container, x, y) {
  const el = document.createElement('div')
  const width = 5 + Math.random() * 4
  const shape = Math.random()
  const height = shape > 0.33 ? width : width * 0.4
  el.style.cssText = `position: absolute; left: ${x}px; top: ${y}px;` +
    `width: ${width}px; height: ${height}px;` +
    `background: ${COLORS[Math.floor(Math.random() * COLORS.length)]};` +
    `border-radius: ${shape > 0.66 ? '50%' : '1px'};`
  container.appendChild(el)
  return el
}

// Falling in from above the top edge, swaying on the way down.
function fall(container, count, span) {
  const w = window.innerWidth
  const h = window.innerHeight

  for (let i = 0; i < count; i++) {
    const el = celebrationPiece(container, Math.random() * w, -20)
    const sway = (Math.random() - 0.5) * 220
    const spin = (Math.random() - 0.5) * 1080

    el.animate([
      { transform: 'translate(0, 0) rotate(0deg)' },
      { transform: `translate(${sway / 2}px, ${h * 0.5}px) rotate(${spin / 2}deg)`, offset: 0.5 },
      { transform: `translate(${sway}px, ${h + 60}px) rotate(${spin}deg)` }
    ], {
      duration: span * (0.55 + Math.random() * 0.45),
      delay: Math.random() * span * 0.28,
      easing: 'linear',
      fill: 'forwards'
    })
  }
}

// Exploding up from below the bottom edge, then falling back through it.
function rise(container, count, span) {
  const w = window.innerWidth
  const h = window.innerHeight

  for (let i = 0; i < count; i++) {
    const el = celebrationPiece(container, Math.random() * w, h + 20)
    const drift = (Math.random() - 0.5) * 320
    const peak = h * (0.45 + Math.random() * 0.45)
    const spin = (Math.random() - 0.5) * 900

    el.animate([
      { transform: 'translate(0, 0) rotate(0deg)' },
      { transform: `translate(${drift * 0.6}px, ${-peak}px) rotate(${spin / 2}deg)`, offset: 0.45 },
      { transform: `translate(${drift}px, ${120 + Math.random() * 120}px) rotate(${spin}deg)` }
    ], {
      duration: span * (0.7 + Math.random() * 0.3),
      delay: Math.random() * 260,
      easing: EASE_OUT,
      fill: 'forwards'
    })
  }
}

// Radial burst from a point, then gravity takes it down. The fall is sized
// per piece so every one of them ends below the bottom edge — with
// fill: forwards, anything whose final position is on screen just sits there.
function burst(container, { x, y }, count, span) {
  const h = window.innerHeight

  for (let i = 0; i < count; i++) {
    const el = celebrationPiece(container, x, y)
    const angle = Math.random() * 2 * Math.PI
    const velocity = 120 + Math.random() * 380
    const dx = Math.cos(angle) * velocity
    const dy = Math.sin(angle) * velocity - 120 // bias the burst upward
    const spin = (Math.random() - 0.5) * 900
    // Clear the bottom edge from wherever the arc peaked.
    const drop = h + 80 - y - dy + Math.random() * 160

    el.animate([
      { transform: 'translate(0, 0) rotate(0deg)' },
      { transform: `translate(${dx}px, ${dy}px) rotate(${spin / 2}deg)`, offset: 0.35 },
      { transform: `translate(${dx * 1.25}px, ${dy + drop}px) rotate(${spin}deg)` }
    ], {
      duration: span * (0.7 + Math.random() * 0.3),
      delay: Math.random() * 120,
      easing: EASE_OUT,
      fill: 'forwards'
    })
  }
}

function burstConfetti({ x, y }) {
  const colors = COLORS
  const container = document.createElement('div')
  container.style.cssText = 'position: fixed; inset: 0; z-index: 2000; pointer-events: none; overflow: hidden;'
  document.body.appendChild(container)

  for (let i = 0; i < 30; i++) {
    const piece = document.createElement('div')
    const size = 5 + Math.random() * 5
    const height = Math.random() > 0.5 ? size * 0.4 : size
    piece.style.cssText = `position: absolute; left: ${x}px; top: ${y}px; width: ${size}px; height: ${height}px;` +
      `background: ${colors[i % colors.length]}; border-radius: ${Math.random() > 0.5 ? '50%' : '1px'};`
    container.appendChild(piece)

    const angle = Math.random() * 2 * Math.PI
    const velocity = 50 + Math.random() * 130
    const dx = Math.cos(angle) * velocity
    const dy = Math.sin(angle) * velocity - 90 // bias the burst upward
    const fall = 120 + Math.random() * 100
    const rotation = (Math.random() - 0.5) * 720
    const duration = 900 + Math.random() * 600

    piece.animate([
      { transform: 'translate(0, 0) rotate(0deg)', opacity: 1 },
      { transform: `translate(${dx}px, ${dy}px) rotate(${rotation / 2}deg)`, opacity: 1, offset: 0.4 },
      { transform: `translate(${dx * 1.3}px, ${dy + fall}px) rotate(${rotation}deg)`, opacity: 0 }
    ], { duration, easing: 'cubic-bezier(0.2, 0.6, 0.4, 1)', fill: 'forwards' })
  }

  setTimeout(() => container.remove(), 1600)
}
